import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import netSuiteLogo from '../../assets/logos/netsuite-logo.svg';
import openAILogo from '../../assets/logos/openai-logo.svg';
import anthropicLogo from '../../assets/logos/anthropic-logo.svg';
import { connectorAPI } from '../../utils/api';
import { 
  faTimes, 
  faEye, 
  faEyeSlash, 
  faNetworkWired,
  faEdit,
  faTrash,
  faCheckCircle,
  faSpinner,
  faCopy,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const ViewConnectorModal = ({ 
  isOpen, 
  onClose, 
  connector,
  onEdit, 
  onDelete, 
  onTest,
  onRefreshData 
}) => {
  const [connectorData, setConnectorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSensitive, setShowSensitive] = useState({});
  const [copyStates, setCopyStates] = useState({});

  // Load detailed connector data when modal opens
  useEffect(() => {
    if (isOpen && connector && onRefreshData) {
      loadConnectorDetails();
    }
  }, [isOpen, connector]);

  const loadConnectorDetails = async () => {
    if (!connector || !onRefreshData) return;
    
    setLoading(true);
    try {
      const response = await onRefreshData(connector.name);
      if (response.success) {
        setConnectorData(response);
      }
    } catch (error) {
      console.error('Failed to load connector details:', error);
      toast.error('Failed to load connector details');
    } finally {
      setLoading(false);
    }
  };

  const toggleSensitive = (field) => {
    setShowSensitive(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStates(prev => ({ ...prev, [field]: true }));
      toast.success('Copied to clipboard');
      setTimeout(() => {
        setCopyStates(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const maskSensitiveValue = (value, field) => {
    if (showSensitive[field]) {
      return value;
    }
    return '*'.repeat(Math.min(value?.length || 0, 12));
  };

  const isSensitiveField = (fieldName) => {
    return ['secret', 'password', 'key', 'token'].some(sensitive => 
      fieldName.toLowerCase().includes(sensitive)
    );
  };

  const getConnectorLogo = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return netSuiteLogo;
      case 'openai':
        return openAILogo;
      case 'anthropic':
        return anthropicLogo;
      default:
        return null;
    }
  };

  const getConnectorName = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return 'NetSuite';
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      default:
        return serviceType;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const renderCredentialField = (key, value) => {
    if (!value) return null;
    
    const isSensitive = isSensitiveField(key);
    const displayValue = isSensitive ? maskSensitiveValue(value, key) : value;

    return (
      <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
            {key.replace(/_/g, ' ')}
          </label>
          <div className="flex items-center space-x-2">
            {isSensitive && (
              <button
                onClick={() => toggleSensitive(key)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon 
                  icon={showSensitive[key] ? faEyeSlash : faEye} 
                  className="text-sm"
                />
              </button>
            )}
            <button
              onClick={() => copyToClipboard(value, key)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <FontAwesomeIcon 
                icon={copyStates[key] ? faCheck : faCopy} 
                className="text-sm"
              />
            </button>
          </div>
        </div>
        <div className="font-mono text-sm text-gray-900 dark:text-white break-all">
          {displayValue}
        </div>
      </div>
    );
  };

  if (!isOpen || !connector) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                  {getConnectorLogo(connector.service_type) ? (
                    <img 
                      src={getConnectorLogo(connector.service_type)} 
                      alt={`${getConnectorName(connector.service_type)} Logo`}
                      className="w-10 h-6 object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white text-xl">
                      🔗
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {connector.name}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getConnectorName(connector.service_type)} Connector
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FontAwesomeIcon icon={faSpinner} className="text-2xl text-gray-400 animate-spin mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading connector details...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                        connector.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {connector.status}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Authentication Type
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white font-mono capitalize">
                        {connector.service_type}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Created
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatTimeAgo(connector.created_at)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Updated
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatTimeAgo(connector.last_tested)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Credentials */}
                {connectorData?.credentials && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Connection Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(connectorData.credentials).map(([key, value]) => 
                        renderCredentialField(key, value)
                      )}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                {connectorData?.metadata && Object.keys(connectorData.metadata).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Additional Information
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <pre className="text-sm text-gray-900 dark:text-white overflow-auto">
                        {JSON.stringify(connectorData.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div className="flex space-x-3">
                <button
                  onClick={() => onTest(connector.name)}
                  className="btn btn-secondary"
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Test Connection
                </button>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    onClose();
                    onEdit(connector);
                  }}
                  className="btn btn-primary"
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this connector?')) {
                      onClose();
                      onDelete(connector.name);
                    }
                  }}
                  className="btn btn-danger"
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  Delete
                </button>
                <button
                  onClick={onClose}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ViewConnectorModal;